import React, { useState, useEffect, useMemo } from "react";

import "./WSPortfolioActivities.scss";
import WSPortfolioActivity from "./WSPortfolioActivity";
import BeautifulSkeleton from "../../../common/Skeletons/BeautifulSkeleton";

// Define the preferred display order for portfolios
const portfolioDisplayOrder = [
  "Dynamic Upside",
  "Steady Guard",
  "Fundamental Edge",
  // Add other portfolios as needed
  "Accelerated Growth",
  "Secure Bluechip",
  "Undervalued Advantage",
  "Momentum Ascent",
  "Resilient Horizon",
  "Hidden Value Discovery",
  "Capital Shield",
  "Steady Climb",
  // Core portfolios
  "Capital Jetstream",
  "Safe Harbor",
  "Robust Anchor",
];

const WSPortfolioActivities = ({
  adjustments,
  loading,
  currentPortfolio = "",
}) => {
  // Set default selection based on currentPortfolio
  const getDefaultPortfolio = () => {
    switch (currentPortfolio.toLowerCase()) {
      case "atlas":
        return "Accelerated Growth";
      case "titan":
        return "Dynamic Upside";
      case "everest":
        return "Momentum Ascent";
      case "core":
        return "Capital Jetstream";
      default:
        return null;
    }
  };

  const [selectedPortfolio, setSelectedPortfolio] = useState(
    getDefaultPortfolio(),
  );
  const [uniquePortfolios, setUniquePortfolios] = useState([]);

  useEffect(() => {
    if (!loading && adjustments) {
      // Extract all unique portfolio names
      const portfolioSet = new Set();
      adjustments.forEach((dateGroup) => {
        dateGroup.adjustments.forEach((adjustment) => {
          if (adjustment.portfolioName) {
            portfolioSet.add(adjustment.portfolioName);
          }
        });
      });
      setUniquePortfolios(Array.from(portfolioSet));
    }
  }, [adjustments, loading]);

  // Update default selection when currentPortfolio changes
  useEffect(() => {
    setSelectedPortfolio(getDefaultPortfolio());
  }, [currentPortfolio]);

  const handlePortfolioSelect = (portfolio) => {
    setSelectedPortfolio(selectedPortfolio === portfolio ? null : portfolio);
  };

  const filteredAdjustments = selectedPortfolio
    ? adjustments
        ?.map((dateGroup) => ({
          ...dateGroup,
          adjustments: dateGroup.adjustments.filter(
            (adjustment) => adjustment.portfolioName === selectedPortfolio,
          ),
        }))
        .filter((dateGroup) => dateGroup.adjustments.length > 0)
    : adjustments;

  // Sort the portfolios for display according to our preferred order
  const sortedPortfolios = useMemo(() => {
    if (!uniquePortfolios.length) return [];

    // Create a copy of the unique portfolios
    const portfoliosCopy = [...uniquePortfolios];

    // Sort the portfolios based on the defined order
    portfoliosCopy.sort((a, b) => {
      const indexA = portfolioDisplayOrder.indexOf(a);
      const indexB = portfolioDisplayOrder.indexOf(b);

      // If both items are in our order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one item is in our order array, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither is in our order array, maintain alphabetical order
      return a.localeCompare(b);
    });

    // Remove the logic that moves selected portfolio to first position
    // Just return the sorted array based on the predefined display order
    return portfoliosCopy;
  }, [uniquePortfolios]);

  return (
    <div className="ws-portfolio-activities">
      <div className="portfolio-selection">
        <div className="portfolio-selection__label">Select Portfolio:</div>
        <div className="portfolio-selection__pills">
          {loading
            ? // Show 5 skeleton pills when loading
              Array.from({ length: 5 }).map((_, index) => (
                <BeautifulSkeleton
                  key={index}
                  variant="rounded"
                  className="portfolio-pill-skeleton"
                  width={120}
                  height={27}
                />
              ))
            : sortedPortfolios.map((portfolio) => (
                <button
                  key={portfolio}
                  className={`portfolio-pill ${selectedPortfolio === portfolio ? "selected" : ""}`}
                  onClick={() => handlePortfolioSelect(portfolio)}
                >
                  {selectedPortfolio === portfolio && (
                    <svg
                      className="check-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 16.2L4.8 12L3.4 13.4L9 19L21 7L19.6 5.6L9 16.2Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                  {portfolio}
                </button>
              ))}
        </div>
      </div>

      {(loading
        ? Array.from({ length: 1 }).fill(null)
        : filteredAdjustments
      )?.map((item, index) => (
        <WSPortfolioActivity key={index} item={item} loading={loading} />
      ))}

      <div className="ws-portfolio-activity__footer">
        No more items to display
      </div>
    </div>
  );
};

export default WSPortfolioActivities;
